import { useEffect, useState, useMemo, useCallback } from 'react';
import { api, formatMoney } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Line } from 'recharts';
import { Download, FileText, TrendUp, Coins, WarningCircle, ChartLine, Sliders, Palette } from '@phosphor-icons/react';
import { toast } from 'sonner';

const AR_MONTHS = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

const AVAILABLE_COLUMNS = [
  { key: 'payment_no', label: 'رقم الدفعة' },
  { key: 'invoice_no', label: 'رقم الفاتورة' },
  { key: 'tenant_name', label: 'المستأجر' },
  { key: 'property_name', label: 'العقار' },
  { key: 'branch', label: 'الفرع' },
  { key: 'city', label: 'المدينة' },
  { key: 'contract_no', label: 'رقم العقد' },
  { key: 'contract_type', label: 'نوع العقد' },
  { key: 'due_date', label: 'تاريخ الاستحقاق' },
  { key: 'period_from', label: 'الفترة من' },
  { key: 'period_to', label: 'الفترة إلى' },
  { key: 'invoice_amount', label: 'المبلغ' },
  { key: 'collected_amount', label: 'المحصل' },
  { key: 'remaining_amount', label: 'المتبقي' },
  { key: 'payment_status', label: 'حالة السداد' },
  { key: 'follow_up_status', label: 'حالة المتابعة' },
  { key: 'phone', label: 'الجوال' },
  { key: 'national_id', label: 'الهوية' },
  { key: 'assigned_user_name', label: 'الموظف' },
  { key: 'collectibility', label: 'قابلية التحصيل' },
];

const COLOR_PRESETS = [
  { name: 'كلاسيكي',  primary: '#0F172A', accent: '#B4935A' },
  { name: 'أزرق',     primary: '#1E3A8A', accent: '#3B82F6' },
  { name: 'أخضر',     primary: '#064E3B', accent: '#059669' },
  { name: 'أحمر',     primary: '#7F1D1D', accent: '#DC2626' },
  { name: 'أرجواني',  primary: '#4C1D95', accent: '#7C3AED' },
  { name: 'وردي',     primary: '#831843', accent: '#DB2777' },
  { name: 'برتقالي',  primary: '#7C2D12', accent: '#EA580C' },
];

const EMPTY_SUMMARY = {
  kpis: { count: 0, total: 0, collected: 0, remaining: 0, rate: 0, overdue: 0, paid: 0, partial: 0, unpaid: 0 },
  by_month: AR_MONTHS.map((m, i) => ({ month: m, m: i + 1, total: 0, collected: 0, rate: 0 })),
  by_branch: [],
};

export default function Reports() {
  const [branches, setBranches] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Filters
  const [period, setPeriod] = useState('yearly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [branch, setBranch] = useState('all');
  const [status, setStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // PDF customization
  const [pdfOpen, setPdfOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ recipients: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [reportTypes, setReportTypes] = useState([]);
  const [pdfOpts, setPdfOpts] = useState({
    report_type: 'quarterly_summary',
    title: '',
    subtitle: '',
    notes: '',
    primary_color: '#0F172A',
    accent_color: '#B4935A',
    orientation: 'portrait',
    page_size: 'A4',
    with_logo: true,
    margins: { top: 22, right: 15, bottom: 20, left: 15 },
    include_kpis: true,
    include_branches: true,
    include_table: false,
    include_status_breakdown: true,
    columns: ['payment_no', 'tenant_name', 'property_name', 'branch', 'due_date', 'invoice_amount', 'collected_amount', 'remaining_amount', 'payment_status'],
  });

  // Build server-side filter params (shared by summary + PDF)
  const buildParams = useCallback(() => {
    const p = { period, year };
    if (period === 'monthly')   p.month = month;
    if (period === 'quarterly') p.quarter = quarter;
    if (period === 'custom') {
      if (fromDate) p.from_date = fromDate;
      if (toDate)   p.to_date = toDate;
    }
    if (branch !== 'all') p.branch = branch;
    if (status !== 'all') p.status = status;
    return p;
  }, [period, year, month, quarter, fromDate, toDate, branch, status]);

  const loadSummary = useCallback(() => {
    setLoading(true);
    api.get('/reports/summary', { params: buildParams() })
      .then(({ data }) => setSummary(data || EMPTY_SUMMARY))
      .catch(() => { setSummary(EMPTY_SUMMARY); toast.error('تعذّر تحميل ملخص التقارير'); })
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => {
    api.get('/reports/types').then(({ data }) => setReportTypes(data)).catch(() => {});
    api.get('/meta/branches').then(({ data }) => setBranches(data)).catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get('/reports/history');
      setHistory(data);
    } catch { toast.error('تعذّر تحميل السجل'); }
  };

  const kpis = summary.kpis || EMPTY_SUMMARY.kpis;
  const chartData = summary.by_month || EMPTY_SUMMARY.by_month;
  const branchPerformance = summary.by_branch || [];

  const periodLabel = useMemo(() => (
    period === 'monthly' ? `${AR_MONTHS[month - 1]} ${year}`
      : period === 'quarterly' ? `الربع ${quarter} · ${year}`
      : period === 'yearly' ? `سنة ${year}`
      : `${fromDate || '—'} إلى ${toDate || '—'}`
  ), [period, month, year, quarter, fromDate, toDate]);

  const generatePDF = async (opts) => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const o = opts || pdfOpts;
      const resp = await fetch(`${api.defaults.baseURL}/reports/pdf/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          report_type: o.report_type || 'custom',
          title: o.title || undefined,
          subtitle: o.subtitle || `${periodLabel}${branch !== 'all' ? ` · فرع ${branch}` : ''}`,
          // Send filter params instead of invoice_ids so backend queries the DB directly
          ...buildParams(),
          notes: o.notes || undefined,
          primary_color: o.primary_color,
          accent_color: o.accent_color,
          orientation: o.orientation,
          page_size: o.page_size,
          with_logo: o.with_logo,
          margins: o.margins,
          columns: o.columns,
          sections: { kpis: o.include_kpis, branches: o.include_branches, table: o.include_table, status_breakdown: o.include_status_breakdown },
        }),
      });
      if (!resp.ok) throw new Error('PDF failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `report-${year}-${period}-${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('تم إنشاء PDF احترافي');
      setPdfOpen(false);
    } catch { toast.error('فشل توليد PDF'); }
    finally { setGenerating(false); }
  };

  const toggleColumn = (k) => {
    setPdfOpts(o => ({ ...o, columns: o.columns.includes(k) ? o.columns.filter(x => x !== k) : [...o.columns, k] }));
  };

  const downloadCsvSummary = () => {
    // Export the aggregated summary (KPIs + branch performance + monthly) — no full list
    const headers = ['قسم', 'مؤشر', 'قيمة'];
    const rows = [
      ['المؤشرات', 'عدد الفواتير', kpis.count],
      ['المؤشرات', 'الإجمالي', kpis.total.toFixed(2)],
      ['المؤشرات', 'المحصل', kpis.collected.toFixed(2)],
      ['المؤشرات', 'المتبقي', kpis.remaining.toFixed(2)],
      ['المؤشرات', 'نسبة التحصيل %', kpis.rate.toFixed(2)],
      ['المؤشرات', 'متأخرة', kpis.overdue],
      ['المؤشرات', 'مسدّدة بالكامل', kpis.paid],
      ['المؤشرات', 'جزئية', kpis.partial],
      ['المؤشرات', 'غير مسدّدة', kpis.unpaid],
      ...branchPerformance.map(b => ['أداء الفروع', b.branch, `إجمالي=${b.total.toFixed(2)} · محصل=${b.collected.toFixed(2)} · نسبة=${b.rate.toFixed(2)}%`]),
      ...chartData.map(m => ['التقدم الشهري', m.month, `إجمالي=${m.total.toFixed(2)} · محصل=${m.collected.toFixed(2)}`]),
    ];
    const csv = '\uFEFF' + [headers, ...rows].map(r => r.map(x => `"${(x ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `report-summary-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const years = [year - 2, year - 1, year, year + 1];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Executive Reports</div>
          <h1 className="text-3xl font-bold text-slate-900">التقارير</h1>
          <p className="text-sm text-slate-500 mt-1">ملخصات تنفيذية بمؤشرات ورسوم بيانية — كل الحسابات على الخادم عبر Aggregation</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openHistory} size="sm" data-testid="reports-history-button">
            <FileText className="ms-1" size={14} /> سجل التقارير
          </Button>
          <Button variant="outline" onClick={downloadCsvSummary} size="sm" data-testid="reports-csv-btn">
            <Download className="ms-1" size={14} /> CSV
          </Button>
          <Button onClick={() => setPdfOpen(true)} className="bg-slate-900 text-white" size="sm" data-testid="customize-pdf-button">
            <Sliders className="ms-1" size={14} /> إنشاء تقرير PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border border-slate-200 rounded-md shadow-none">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">الفترة</label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-9" data-testid="report-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">شهري</SelectItem>
                <SelectItem value="quarterly">ربع سنوي</SelectItem>
                <SelectItem value="yearly">سنوي</SelectItem>
                <SelectItem value="custom">بين تاريخين</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period !== 'custom' && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">السنة</label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="h-9" data-testid="report-year"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {period === 'monthly' && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">الشهر</label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="h-9" data-testid="report-month"><SelectValue /></SelectTrigger>
                <SelectContent>{AR_MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {period === 'quarterly' && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">الربع</label>
              <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
                <SelectTrigger className="h-9" data-testid="report-quarter"><SelectValue /></SelectTrigger>
                <SelectContent>{[1,2,3,4].map(q => <SelectItem key={q} value={String(q)}>الربع {q}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {period === 'custom' && (
            <>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">من</label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9" data-testid="report-from-date" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">إلى</label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9" data-testid="report-to-date" />
              </div>
            </>
          )}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">الفرع</label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="h-9" data-testid="report-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الفروع</SelectItem>
                {branches.map(b => <SelectItem key={b.code} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">حالة السداد</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9" data-testid="report-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الحالات</SelectItem>
                <SelectItem value="Fully Paid">مدفوعة بالكامل</SelectItem>
                <SelectItem value="Partially Paid">مدفوعة جزئياً</SelectItem>
                <SelectItem value="Unpaid">غير مدفوعة</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="إجمالي المبالغ"    value={formatMoney(kpis.total)}     icon={Coins}         color="#0F172A" testId="kpi-total" />
        <KpiCard label="إجمالي المحصل"    value={formatMoney(kpis.collected)} icon={TrendUp}       color="#059669" testId="kpi-collected" />
        <KpiCard label="نسبة التحصيل"     value={`${(kpis.rate || 0).toFixed(1)}%`} icon={ChartLine} color="#B4935A" testId="kpi-rate" />
        <KpiCard label="فواتير متأخرة"    value={kpis.overdue.toLocaleString()} icon={WarningCircle} color="#DC2626" testId="kpi-overdue" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="عدد الفواتير"      value={kpis.count.toLocaleString()}   size="sm" testId="kpi-count" />
        <KpiCard label="المتبقي"           value={formatMoney(kpis.remaining)} size="sm" color="#DC2626" testId="kpi-remaining" />
        <KpiCard label="مغلقة"             value={kpis.paid.toLocaleString()}   size="sm" color="#059669" testId="kpi-paid" />
        <KpiCard label="جزئية / غير مسددة" value={`${kpis.partial} / ${kpis.unpaid}`} size="sm" testId="kpi-partial-unpaid" />
      </div>

      {loading && <div className="text-sm text-slate-500 text-center py-2">جاري احتساب الملخّص على الخادم...</div>}

      {/* Monthly Progress Chart */}
      <Card className="p-5 border border-slate-200 rounded-md shadow-none" data-testid="progress-chart">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-[#B4935A]">Collection Progress</div>
          <div className="text-lg font-semibold text-slate-900">تقدم التحصيل الشهري — {year}{branch !== 'all' ? ` · ${branch}` : ''}</div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748B' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#B4935A' }} domain={[0, 100]} unit="%" />
            <Tooltip contentStyle={{ fontSize: 12, direction: 'rtl' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="total" name="الإجمالي" fill="#CBD5E1" radius={[3, 3, 0, 0]} />
            <Bar yAxisId="left" dataKey="collected" name="المحصل" fill="#0F172A" radius={[3, 3, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="rate" name="نسبة التحصيل %" stroke="#B4935A" strokeWidth={3} dot={{ r: 4, fill: '#B4935A' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* Branch Performance */}
      {branchPerformance.length > 0 && (
        <Card className="p-5 border border-slate-200 rounded-md shadow-none" data-testid="branch-chart">
          <div className="mb-4">
            <div className="text-xs uppercase tracking-widest text-[#B4935A]">Branch Performance</div>
            <div className="text-lg font-semibold text-slate-900">أداء الفروع في الفترة المحددة</div>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(240, branchPerformance.length * 40)}>
            <BarChart data={branchPerformance} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis type="category" dataKey="branch" tick={{ fontSize: 11, fill: '#64748B' }} width={80} />
              <Tooltip contentStyle={{ fontSize: 12, direction: 'rtl' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="collected" name="المحصل" fill="#B4935A" radius={[0, 3, 3, 0]} />
              <Bar dataKey="total" name="الإجمالي" fill="#CBD5E1" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* PDF Customization Dialog */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>تخصيص تقرير PDF</DialogTitle>
            <DialogDescription>عدّل العنوان، الألوان، الاتجاه، والأعمدة قبل التصدير</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="mb-1 block">نوع التقرير</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {reportTypes.map(t => (
                  <button
                    key={t.code}
                    type="button"
                    onClick={() => {
                      const defaults = {
                        quarterly_summary: { include_kpis: true, include_branches: true, include_table: false, include_status_breakdown: true, orientation: 'portrait' },
                        yearly_summary:    { include_kpis: true, include_branches: true, include_table: false, include_status_breakdown: true, orientation: 'portrait' },
                        monthly_summary:   { include_kpis: true, include_branches: true, include_table: false, include_status_breakdown: true, orientation: 'portrait' },
                        overdue_report:    { include_kpis: true, include_branches: false, include_table: true, include_status_breakdown: false, orientation: 'landscape' },
                        detailed_list:     { include_kpis: false, include_branches: false, include_table: true, include_status_breakdown: false, orientation: 'landscape' },
                        custom:            {},
                      }[t.code] || {};
                      setPdfOpts(o => ({ ...o, report_type: t.code, ...defaults }));
                    }}
                    className={`text-start p-3 rounded-md border transition-colors ${pdfOpts.report_type === t.code ? 'border-[#B4935A] bg-[#B4935A]/10 ring-1 ring-[#B4935A]' : 'border-slate-200 hover:border-slate-400'}`}
                    data-testid={`rpt-type-${t.code}`}
                  >
                    <div className="font-medium text-sm text-slate-900">{t.label}</div>
                    <div className="text-xs text-slate-500 mt-1 leading-relaxed">{t.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>عنوان التقرير</Label>
                <Input value={pdfOpts.title} onChange={(e) => setPdfOpts({ ...pdfOpts, title: e.target.value })} data-testid="pdf-title-input" />
              </div>
              <div>
                <Label>العنوان الفرعي</Label>
                <Input value={pdfOpts.subtitle} onChange={(e) => setPdfOpts({ ...pdfOpts, subtitle: e.target.value })} placeholder="سيُملأ تلقائياً حسب الفلاتر إن ترك فارغاً" />
              </div>
              <div>
                <Label>ملاحظات في نهاية التقرير</Label>
                <Textarea rows={2} value={pdfOpts.notes} onChange={(e) => setPdfOpts({ ...pdfOpts, notes: e.target.value })} />
              </div>
              <div>
                <Label>اتجاه الصفحة</Label>
                <Select value={pdfOpts.orientation} onValueChange={(v) => setPdfOpts({ ...pdfOpts, orientation: v })}>
                  <SelectTrigger data-testid="orientation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">طولي (Portrait)</SelectItem>
                    <SelectItem value="landscape">عرضي (Landscape)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>حجم الورق</Label>
                <Select value={pdfOpts.page_size} onValueChange={(v) => setPdfOpts({ ...pdfOpts, page_size: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A4">A4</SelectItem>
                    <SelectItem value="Letter">Letter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-2 block flex items-center gap-2"><Palette size={16} className="text-[#B4935A]" /> ألوان التقرير</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {COLOR_PRESETS.map(p => (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => setPdfOpts({ ...pdfOpts, primary_color: p.primary, accent_color: p.accent })}
                    className={`px-3 py-1.5 rounded-md border text-xs flex items-center gap-2 ${pdfOpts.primary_color === p.primary ? 'ring-2 ring-offset-2 ring-slate-900' : 'border-slate-200'}`}
                  >
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: p.primary }} />
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: p.accent }} />
                    {p.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">اللون الأساسي</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={pdfOpts.primary_color} onChange={(e) => setPdfOpts({ ...pdfOpts, primary_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                    <Input value={pdfOpts.primary_color} onChange={(e) => setPdfOpts({ ...pdfOpts, primary_color: e.target.value })} className="mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">اللون المميّز (Accent)</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={pdfOpts.accent_color} onChange={(e) => setPdfOpts({ ...pdfOpts, accent_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                    <Input value={pdfOpts.accent_color} onChange={(e) => setPdfOpts({ ...pdfOpts, accent_color: e.target.value })} className="mono" />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">الأقسام المُضمّنة</Label>
              <div className="flex flex-wrap gap-4">
                {[
                  { k: 'include_kpis', label: 'الملخص التنفيذي (KPIs)' },
                  { k: 'include_status_breakdown', label: 'توزيع حالات السداد' },
                  { k: 'include_branches', label: 'أداء الفروع' },
                  { k: 'include_table', label: 'جدول تفاصيل الفواتير' },
                ].map(s => (
                  <label key={s.k} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox checked={pdfOpts[s.k]} onCheckedChange={(v) => setPdfOpts({ ...pdfOpts, [s.k]: !!v })} />
                    {s.label}
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox checked={pdfOpts.with_logo} onCheckedChange={(v) => setPdfOpts({ ...pdfOpts, with_logo: !!v })} />
                  إظهار شعار الشركة
                </label>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">هوامش الصفحة (mm)</Label>
              <div className="grid grid-cols-4 gap-2">
                {['top','right','bottom','left'].map(side => (
                  <div key={side}>
                    <div className="text-xs text-slate-500 mb-1">{{top:'علوي',right:'يمين',bottom:'سفلي',left:'يسار'}[side]}</div>
                    <Input type="number" min="5" max="50" value={pdfOpts.margins[side]}
                      onChange={(e) => setPdfOpts({ ...pdfOpts, margins: { ...pdfOpts.margins, [side]: Number(e.target.value) || 0 } })}
                      data-testid={`margin-${side}`} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">أعمدة الجدول <span className="text-xs text-slate-500">(اختر ما يظهر — الترتيب حسب النقر)</span></Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 border border-slate-200 rounded-md p-3 max-h-48 overflow-y-auto">
                {AVAILABLE_COLUMNS.map(c => (
                  <label key={c.key} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-slate-50 p-1 rounded">
                    <Checkbox checked={pdfOpts.columns.includes(c.key)} onCheckedChange={() => toggleColumn(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-1">{pdfOpts.columns.length} عمود مختار</div>
            </div>

            <Button onClick={() => generatePDF(pdfOpts)} disabled={generating} className="w-full bg-slate-900 text-white h-11" data-testid="generate-custom-pdf">
              <FileText className="ms-1" size={16} /> {generating ? 'جاري التوليد...' : 'إنشاء وتنزيل PDF المخصص'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reports History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>سجل التقارير</DialogTitle>
            <DialogDescription>كل تقرير تم إصداره من النظام مع رقمه، مُصدِره، والتوقيت</DialogDescription>
          </DialogHeader>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-start p-2 text-xs uppercase tracking-wider text-slate-600">رقم التقرير</th>
                <th className="text-start p-2 text-xs uppercase tracking-wider text-slate-600">النوع</th>
                <th className="text-start p-2 text-xs uppercase tracking-wider text-slate-600">العنوان</th>
                <th className="text-start p-2 text-xs uppercase tracking-wider text-slate-600">المُصدِر</th>
                <th className="text-start p-2 text-xs uppercase tracking-wider text-slate-600">التاريخ والوقت</th>
                <th className="text-end p-2 text-xs uppercase tracking-wider text-slate-600">فواتير</th>
                <th className="text-end p-2 text-xs uppercase tracking-wider text-slate-600">نسبة التحصيل</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={8} className="text-center p-6 text-slate-400">لا توجد تقارير بعد</td></tr>
              ) : history.map(h => (
                <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-2 mono font-semibold text-slate-900">{h.report_no}</td>
                  <td className="p-2 text-slate-600">{h.report_type_label || h.report_type}</td>
                  <td className="p-2">{h.title}</td>
                  <td className="p-2 text-slate-600">{h.generated_by_name || h.user_name || '—'}</td>
                  <td className="p-2 text-slate-500 text-xs mono">{h.generated_at ? new Date(h.generated_at).toLocaleString('ar-SA', { hour12: false }) : '—'}</td>
                  <td className="p-2 text-end tabular-nums">{Number(h.invoices_count || h.invoice_count || 0).toLocaleString()}</td>
                  <td className="p-2 text-end tabular-nums">{Number(h.collection_rate || 0).toFixed(1)}%</td>
                  <td className="p-2 flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" title="تنزيل" onClick={() => {
                      const token = localStorage.getItem('token');
                      window.open(`${api.defaults.baseURL}/reports/history/${h.id}/download?auth=${encodeURIComponent(token)}`, '_blank');
                    }}><Download size={14} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>

      {/* Email dialog placeholder (kept for future use) */}
      {emailOpen && emailTarget && (
        <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
          <DialogContent><DialogHeader><DialogTitle>إرسال بالبريد</DialogTitle></DialogHeader></DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const KpiCard = ({ label, value, icon: Icon, color = '#0F172A', size = 'lg', testId }) => (
  <Card className={`border border-slate-200 rounded-md shadow-none ${size === 'sm' ? 'p-3' : 'p-4'}`} data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className={`text-xs text-slate-500 ${size === 'sm' ? '' : 'mb-1'}`}>{label}</div>
        <div className={`font-bold tabular-nums ${size === 'sm' ? 'text-lg' : 'text-2xl'}`} style={{ color }}>{value}</div>
      </div>
      {Icon && <Icon size={size === 'sm' ? 20 : 28} style={{ color }} />}
    </div>
  </Card>
);
